import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export interface StaffOption {
  value: number;
  label: string;
  /** Pre-lowercased haystack: name + location + role + anything else searchable. */
  search: string;
}

interface Props {
  staff: StaffOption[];
  /** Currently selected staff IDs. Empty array = "All Staff". */
  value: number[];
  onChange: (ids: number[]) => void;
  width?: number | string | { [k: string]: number | string };
  loading?: boolean;
}

/**
 * Searchable multi-select Staff filter. Mirrors GradeMultiSelect so the three
 * class-schedule filters share one visual + interaction pattern:
 *   - sticky search box with search icon
 *   - checkboxes per row, menu stays open across selections
 *   - Select All / Clear All
 *   - empty state when the search text has no matches
 * Search is cleared when the dropdown closes.
 */
const StaffMultiSelect: React.FC<Props> = ({ staff, value, onChange, width = 180, loading }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  // Reset the search text when the menu closes so the next open starts fresh.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    staff.forEach((s) => m.set(Number(s.value), s.label));
    return m;
  }, [staff]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? staff.filter((s) => s.search.includes(q)) : staff;
  }, [staff, search]);

  const renderValue = (selected: number[]) => {
    if (!selected || selected.length === 0) return "All Staff";
    if (selected.length === 1) return nameById.get(Number(selected[0])) ?? "1 staff";
    const names = selected
      .map((id) => nameById.get(Number(id)))
      .filter(Boolean) as string[];
    if (selected.length === 2 && names.join(", ").length <= 22) return names.join(", ");
    return `Staff · ${selected.length}`;
  };

  const allIds = useMemo(() => staff.map((s) => Number(s.value)), [staff]);

  return (
    <FormControl size="small" sx={{ width }}>
      <InputLabel shrink>Staff</InputLabel>
      <Select
        multiple
        notched
        displayEmpty
        label="Staff"
        value={value}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={(e) => {
          const v = e.target.value;
          onChange(typeof v === "string" ? [] : (v as number[]).map(Number));
        }}
        renderValue={(selected) => renderValue(selected as number[])}
        sx={{ borderRadius: "10px" }}
        MenuProps={{
          autoFocus: false,
          PaperProps: { style: { maxHeight: 360, width: 300 } },
        }}
      >
        {/* Non-selectable header — ListSubheader stays sticky at the top of
            the scrollable list and never participates in the Select value. */}
        <ListSubheader sx={{ bgcolor: "background.paper", px: 1, pt: 1, pb: 0.5, borderBottom: "1px solid rgba(15,23,42,0.06)" }}>
          <TextField
            size="small"
            autoFocus
            fullWidth
            placeholder="Search staff…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            // Stop keystrokes from bubbling into Select's type-ahead handler,
            // which would move focus to a matching MenuItem on each letter.
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: "text.disabled" }} /> }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
            <Button size="small" onClick={() => onChange(allIds)} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }} disabled={loading || staff.length === 0}>
              Select All
            </Button>
            <Button size="small" onClick={() => onChange([])} sx={{ textTransform: "none", fontSize: 11.5, minWidth: 0 }} disabled={value.length === 0}>
              Clear All
            </Button>
          </Box>
        </ListSubheader>

        {loading && filteredStaff.length === 0 && (
          <MenuItem disabled>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Loading…</Typography>
          </MenuItem>
        )}

        {!loading && filteredStaff.length === 0 && (
          <MenuItem disabled>
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>No staff found</Typography>
          </MenuItem>
        )}

        {filteredStaff.map((s) => {
          const id = Number(s.value);
          return (
            <MenuItem key={id} value={id} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={value.includes(id)} sx={{ py: 0.25, mr: 0.5 }} />
              <ListItemText primary={s.label} primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export default StaffMultiSelect;
